import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DOMPurify from "dompurify";
import { supabase, KoreanBriefRow } from "../../lib/supabase";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import SidebarProductScroller from "../home/components/SidebarProductScroller";
import SidebarCompanyScroller from "../home/components/SidebarCompanyScroller";
import SEO from "@/components/SEO";
import { ArticleSchema, BreadcrumbSchema } from "@/components/StructuredData";

/**
 * Auto-detect bare URLs in TEXT nodes only (not inside HTML attributes).
 */
const autoLinkContent = (html: string): string => {
  const parts = html.split(/(<[^>]+>)/g);
  return parts
    .map((part) => {
      if (part.startsWith("<") && part.endsWith(">")) return part;
      return part.replace(
        /(https?:\/\/[^\s<>"']+|www\.[^\s<>"']+)/gi,
        (url) => {
          const href = url.startsWith("http") ? url : `https://${url}`;
          return `<a href="${href}" target="_blank" rel="noopener noreferrer nofollow">${url}</a>`;
        }
      );
    })
    .join("");
};

const prepareContent = (content: string): string => {
  if (!content) return "";
  let html = content;
  const hasHtmlTags = /<[a-z][\s\S]*>/i.test(content);
  if (!hasHtmlTags) {
    html = content
      .split(/\n\n+/)
      .map((para) => `<p>${para.replace(/\n/g, "<br>")}</p>`)
      .join("");
  }
  html = autoLinkContent(html);
  html = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      "p", "br", "b", "strong", "i", "em", "u", "s", "strike",
      "h1", "h2", "h3", "h4", "h5", "h6",
      "ul", "ol", "li",
      "blockquote", "pre", "code",
      "a", "img",
      "span", "div",
      "table", "thead", "tbody", "tr", "th", "td",
      "hr",
    ],
    ALLOWED_ATTR: [
      "href", "src", "alt", "title", "target", "rel",
      "style", "class",
      "width", "height",
      "colspan", "rowspan",
    ],
    ALLOW_DATA_ATTR: false,
    FORBID_ATTR: ["onerror", "onload", "onclick", "onmouseover"],
    ADD_ATTR: ["target"],
  });
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  doc.querySelectorAll("a").forEach((a) => {
    a.setAttribute("target", "_blank");
    a.setAttribute("rel", "noopener noreferrer nofollow");
  });
  doc.querySelectorAll("img").forEach((img) => {
    img.setAttribute("onerror", "this.style.display='none'");
    img.style.maxWidth = "100%";
    img.style.borderRadius = "8px";
    img.style.height = "auto";
  });
  return doc.body.innerHTML;
};

const KOREA_CARBON_NANO_LOGO = "https://storage.readdy-site.link/project_files/5f2724b2-0dd8-4526-ab2c-c1f8f3f4c6d1/ff2930e2-71a8-4589-bb2f-65a564d9509d_---.png?v=e95b8b61ae78d1ec4a164b9ad07adef2";

const KoreanBriefDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { i18n } = useTranslation();
  const isKr = i18n.language === "kr";
  const navigate = useNavigate();

  const [article, setArticle] = useState<KoreanBriefRow | null>(null);
  const [allArticles, setAllArticles] = useState<KoreanBriefRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [lightboxImg, setLightboxImg] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    const fetchArticle = async () => {
      setLoading(true);
      const { data } = await supabase
        .from("korean_brief")
        .select("*")
        .eq("id", id)
        .maybeSingle();

      if (!data) {
        navigate("/korean-brief", { replace: true });
        return;
      }

      setArticle(data as KoreanBriefRow);

      // Fetch ALL published articles (excluding current)
      const { data: all } = await supabase
        .from("korean_brief")
        .select("*")
        .eq("is_published", true)
        .neq("id", id)
        .order("published_at", { ascending: false })
        .limit(30);
      setAllArticles((all as KoreanBriefRow[]) ?? []);
      setLoading(false);
    };
    fetchArticle();
  }, [id, navigate]);

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("vi-VN", {
      day: "2-digit", month: "2-digit", year: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F9FA]">
        <Navbar />
        <div className="max-w-[800px] mx-auto px-6 py-16 animate-pulse space-y-6">
          <div className="h-4 bg-gray-200 rounded w-1/4" />
          <div className="h-8 bg-gray-200 rounded" />
          <div className="h-64 bg-gray-200 rounded-2xl" />
          <div className="space-y-3">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-4 bg-gray-200 rounded" style={{ width: `${85 + Math.random() * 15}%` }} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!article) return null;

  const galleryImages =
    article.gallery && article.gallery.length > 0
      ? article.gallery
      : article.image_url
      ? [article.image_url]
      : [];

  const siteUrl = typeof window !== "undefined" ? `${window.location.protocol}//${window.location.host}` : "";

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <SEO
        title={`${article.title} | Korean Brief KCANIA`}
        description={article.subtitle || `${article.title}. Tin tức kinh tế và công nghệ Hàn Quốc cập nhật hàng ngày dành cho doanh nghiệp Việt Nam.`}
        keywords={`${article.title}, Korean Brief, kinh tế Hàn Quốc, công nghệ Hàn Quốc${article.category ? `, ${article.category}` : ""}${article.tags ? `, ${article.tags.join(", ")}` : ""}`}
        ogImage={article.image_url || undefined}
        ogType="article"
        articlePublishedTime={article.published_at || undefined}
        articleAuthor={article.author || article.source_name || "KCANIA Korean Brief"}
        articleSection={article.category || "Korean Brief"}
        articleTags={article.tags || []}
      />
      <ArticleSchema
        headline={article.title}
        description={article.subtitle || undefined}
        image={article.image_url || undefined}
        datePublished={article.published_at || undefined}
        author={article.author || article.source_name || "KCANIA"}
        url={window.location.href}
        keywords={article.tags || []}
      />
      <BreadcrumbSchema
        items={[
          { name: "Trang chủ", url: siteUrl || "/" },
          { name: "Korean Brief", url: `${siteUrl}/korean-brief` },
          { name: article.title, url: window.location.href },
        ]}
      />

      <Navbar />

      {/* Announcement Bar + Pulse Logo */}
      <div className="bg-[#004E98] relative z-30">
        <div className="max-w-[1400px] mx-auto px-6 flex items-center justify-center gap-3 py-2.5 relative">
          <p className="text-white text-sm text-center">
            Nhận tư vấn về sản phẩm công nghệ và kỹ thuật Hàn Quốc - <Link to="/danh-muc" className="text-[#FACC15] font-semibold hover:underline cursor-pointer">Khám phá ngay</Link>
          </p>
        </div>
      </div>

      {/* 3-column layout: Left Sidebar | Main Content | Right Sidebar */}
      <div className="flex items-stretch relative">

        {/* LEFT SIDEBAR — Product Scroller (xl+ only) */}
        <aside className="hidden xl:flex flex-shrink-0 w-[180px]" aria-label="Sản phẩm nổi bật">
          <div className="sticky top-[72px] self-start w-[180px]" style={{ height: "calc(100vh - 72px)" }}>
            <SidebarProductScroller isKr={isKr} />
          </div>
        </aside>

        {/* MAIN CONTENT */}
        <div className="flex-1 min-w-0">
          <div className="max-w-[1100px] mx-auto px-4 py-10">
            {/* Breadcrumb */}
            <nav aria-label="Đường dẫn điều hướng" className="flex items-center gap-2 text-xs text-gray-400 mb-8">
              <Link to="/" className="hover:text-[#004A99] cursor-pointer">
                {isKr ? "홈" : "Trang chủ"}
              </Link>
              <i className="ri-arrow-right-s-line" aria-hidden="true" />
              <Link to="/korean-brief" className="hover:text-[#004A99] cursor-pointer">
                Korean Brief
              </Link>
              <i className="ri-arrow-right-s-line" aria-hidden="true" />
              <span className="text-gray-500 line-clamp-1">{article.title}</span>
            </nav>

            <div className="flex flex-col lg:flex-row gap-8">
              {/* Article body */}
              <article className="flex-1 min-w-0" itemScope itemType="https://schema.org/Article">
                {/* Category & Meta */}
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  {article.category && (
                    <span className="text-xs font-bold text-[#004A99] bg-[#004A99]/10 px-3 py-1 rounded-full">
                      {article.category}
                    </span>
                  )}
                  {article.is_featured && (
                    <span className="text-xs font-bold text-amber-600 bg-amber-50 px-2 py-1 rounded-full">HOT</span>
                  )}
                  <time className="text-xs text-gray-400 ml-auto" dateTime={article.published_at || ""} itemProp="datePublished">
                    {formatDate(article.published_at)}
                  </time>
                </div>

                {/* Title */}
                <h1
                  className="text-2xl lg:text-3xl font-black text-gray-800 leading-tight mb-4"
                  style={{ letterSpacing: "-0.02em" }}
                  itemProp="headline"
                >
                  {article.title}
                </h1>

                {/* Subtitle */}
                {article.subtitle && (
                  <p className="text-base text-gray-600 leading-relaxed mb-6 font-medium border-l-4 border-[#00AEEF] pl-4" itemProp="description">
                    {article.subtitle}
                  </p>
                )}

                {/* Source Info */}
                {(article.source_name || article.author) && (
                  <div className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-3 mb-6 text-sm text-gray-500">
                    <i className="ri-newspaper-line text-[#004A99]" aria-hidden="true" />
                    {article.source_name && (
                      <span className="font-medium text-gray-700" itemProp="publisher">{article.source_name}</span>
                    )}
                    {article.author && <span itemProp="author">by {article.author}</span>}
                    {article.source_url && (
                      <a
                        href={article.source_url}
                        target="_blank"
                        rel="noopener noreferrer nofollow"
                        className="ml-auto flex items-center gap-1 text-xs text-[#004A99] font-semibold hover:underline cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-external-link-line" aria-hidden="true" />
                        {isKr ? "원문 보기" : "Xem bài gốc"}
                      </a>
                    )}
                  </div>
                )}

                {/* Hero Image */}
                {article.image_url && (
                  <div
                    className="w-full h-80 lg:h-96 rounded-2xl overflow-hidden mb-8 cursor-pointer"
                    onClick={() => setLightboxImg(article.image_url!)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === "Enter" && setLightboxImg(article.image_url!)}
                    aria-label="Xem ảnh phóng to"
                  >
                    <img
                      src={article.image_url}
                      alt={article.title}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                      itemProp="image"
                    />
                  </div>
                )}

                {/* Content */}
                {article.content && (
                  <div
                    className="article-content mb-8"
                    dangerouslySetInnerHTML={{ __html: prepareContent(article.content) }}
                    itemProp="articleBody"
                  />
                )}

                <style>{`
                  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
                  @keyframes pulse-scale {
                    0%, 100% { transform: scale(1); }
                    50% { transform: scale(1.08); }
                  }
                  .animate-pulse-scale {
                    animation: pulse-scale 2s ease-in-out infinite;
                  }
                  .article-content {
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                    font-size: 15px;
                    line-height: 1.8;
                    color: #374151;
                    white-space: pre-wrap;
                  }
                  .article-content p { margin: 0.75em 0; min-height: 1.2em; }
                  .article-content h1 { font-size: 1.75em; font-weight: 700; margin: 1em 0 0.5em; color: #111827; line-height: 1.3; }
                  .article-content h2 { font-size: 1.4em; font-weight: 700; margin: 0.9em 0 0.45em; color: #111827; line-height: 1.35; }
                  .article-content h3 { font-size: 1.2em; font-weight: 600; margin: 0.8em 0 0.4em; color: #1f2937; line-height: 1.4; }
                  .article-content b, .article-content strong { font-weight: 700; color: #111827; }
                  .article-content i, .article-content em { font-style: italic; }
                  .article-content u { text-decoration: underline; }
                  .article-content s, .article-content strike { text-decoration: line-through; }
                  .article-content ul { list-style-type: disc; padding-left: 1.5em; margin: 0.75em 0; }
                  .article-content ol { list-style-type: decimal; padding-left: 1.5em; margin: 0.75em 0; }
                  .article-content li { margin: 0.35em 0; }
                  .article-content blockquote {
                    border-left: 4px solid #004A99;
                    padding: 10px 16px;
                    color: #4b5563;
                    font-style: italic;
                    margin: 1em 0;
                    background: #f8f9fa;
                    border-radius: 0 8px 8px 0;
                  }
                  .article-content a {
                    color: #0066cc;
                    text-decoration: underline;
                    cursor: pointer;
                    transition: color 0.15s ease;
                    word-break: break-all;
                  }
                  .article-content a:hover { color: #004A99; }
                  .article-content img { max-width: 100%; height: auto; border-radius: 8px; margin: 8px 0; }
                  .article-content pre, .article-content code {
                    background: #f3f4f6;
                    border-radius: 6px;
                    padding: 2px 6px;
                    font-family: 'Courier New', monospace;
                    font-size: 0.9em;
                  }
                  .article-content pre { padding: 12px 16px; overflow-x: auto; margin: 0.75em 0; }
                  .article-content hr { border: none; border-top: 1px solid #e5e7eb; margin: 1.5em 0; }
                  .article-content table { width: 100%; border-collapse: collapse; margin: 1em 0; font-size: 0.9em; }
                  .article-content th, .article-content td { border: 1px solid #e5e7eb; padding: 8px 12px; text-align: left; }
                  .article-content th { background: #f9fafb; font-weight: 600; }
                `}</style>

                {/* Gallery */}
                {galleryImages.length > 1 && (
                  <div className="mb-8">
                    <h3 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
                      <i className="ri-gallery-line text-[#004A99]" aria-hidden="true" />
                      {isKr ? "사진 갤러리" : "Thư viện ảnh"}
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {galleryImages.map((img, i) => (
                        <div
                          key={i}
                          className="aspect-video rounded-xl overflow-hidden cursor-pointer group"
                          onClick={() => setLightboxImg(img)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => e.key === "Enter" && setLightboxImg(img)}
                          aria-label={`Xem ảnh ${i + 1}`}
                        >
                          <img
                            src={img}
                            alt={`${article.title} – ảnh ${i + 1}`}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                            loading="lazy"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Tags */}
                {article.tags && article.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-6">
                    {article.tags.map((tag) => (
                      <span key={tag} className="text-xs text-gray-600 bg-gray-100 px-3 py-1 rounded-full">
                        #{tag}
                      </span>
                    ))}
                  </div>
                )}

                {/* Source Link CTA */}
                {article.source_url && (
                  <div className="bg-[#004A99]/5 border border-[#004A99]/15 rounded-2xl p-5 mb-6">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center bg-[#004A99] rounded-xl flex-shrink-0">
                        <i className="ri-external-link-line text-white" aria-hidden="true" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-bold text-[#004A99] mb-1">
                          {isKr ? "원문 기사 보기" : "Xem bài báo gốc"}
                        </p>
                        <p className="text-xs text-gray-500 mb-3">
                          {isKr
                            ? "원본 출처에서 toàn bộ bài viết"
                            : "Đọc toàn bộ bài viết trên trang nguồn gốc"}
                        </p>
                        <a
                          href={article.source_url}
                          target="_blank"
                          rel="noopener noreferrer nofollow"
                          className="inline-flex items-center gap-2 px-4 py-2 bg-[#004A99] text-white text-sm font-semibold rounded-lg hover:bg-[#003d7d] transition-colors cursor-pointer"
                        >
                          <i className="ri-external-link-line" aria-hidden="true" />
                          {article.source_name ?? (isKr ? "원문 보기" : "Xem nguồn")}
                          <i className="ri-arrow-right-line text-xs" aria-hidden="true" />
                        </a>
                      </div>
                    </div>
                  </div>
                )}

                {/* Back Button */}
                <Link
                  to="/korean-brief"
                  className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-[#004A99] transition-colors cursor-pointer group"
                >
                  <i className="ri-arrow-left-line group-hover:-translate-x-0.5 transition-transform" aria-hidden="true" />
                  {isKr ? "목록으로 돌아가기" : "Quay lại danh sách"}
                </Link>
              </article>

              {/* Right sidebar — All articles list */}
              <aside className="w-full lg:w-64 flex-shrink-0 space-y-4" aria-label="Tất cả bài viết Korean Brief">
                <div className="bg-white rounded-2xl overflow-hidden">
                  {/* Header */}
                  <div className="bg-gradient-to-r from-[#004A99] to-[#002d5f] px-4 py-3 flex items-center gap-2">
                    <i className="ri-newspaper-line text-white text-base" aria-hidden="true" />
                    <p className="text-white font-bold text-sm">
                      {isKr ? "전체 기사" : "Tất cả bài viết"}
                    </p>
                    {allArticles.length > 0 && (
                      <span className="ml-auto text-white/60 text-xs">{allArticles.length} {isKr ? "개" : "bài"}</span>
                    )}
                  </div>

                  {/* Articles list */}
                  <div className="divide-y divide-gray-50 max-h-[600px] overflow-y-auto">
                    {allArticles.length === 0 ? (
                      <div className="px-4 py-8 text-center text-gray-400 text-xs">
                        <i className="ri-newspaper-line text-2xl block mb-2 text-gray-200" aria-hidden="true" />
                        {isKr ? "다른 기사가 없습니다" : "Chưa có bài viết khác"}
                      </div>
                    ) : (
                      allArticles.map((art) => (
                        <Link
                          key={art.id}
                          to={`/korean-brief/${art.id}`}
                          className="flex gap-3 px-4 py-3 hover:bg-[#004A99]/5 transition-colors group cursor-pointer"
                        >
                          {/* Thumbnail */}
                          <div className="w-14 h-14 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100">
                            {art.image_url ? (
                              <img
                                src={art.image_url}
                                alt={art.title}
                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                                loading="lazy"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#004A99]/10 to-[#00AEEF]/10">
                                <i className="ri-newspaper-line text-[#004A99]/30 text-lg" aria-hidden="true" />
                              </div>
                            )}
                          </div>

                          {/* Info */}
                          <div className="flex-1 min-w-0">
                            {art.category && (
                              <span className="text-[9px] font-bold text-[#004A99] bg-[#004A99]/10 px-1.5 py-0.5 rounded-full inline-block mb-1">
                                {art.category}
                              </span>
                            )}
                            <p className="text-xs font-semibold text-gray-700 group-hover:text-[#004A99] line-clamp-2 leading-snug transition-colors">
                              {art.title}
                            </p>
                            <time className="text-[10px] text-gray-400 mt-1 block" dateTime={art.published_at || ""}>
                              {art.published_at ? formatDate(art.published_at) : ""}
                            </time>
                          </div>
                        </Link>
                      ))
                    )}
                  </div>

                  {/* Footer link */}
                  <div className="border-t border-gray-100 px-4 py-3">
                    <Link
                      to="/korean-brief"
                      className="flex items-center justify-center gap-2 text-xs text-[#004A99] font-semibold hover:underline cursor-pointer"
                    >
                      <i className="ri-list-check-2" aria-hidden="true" />
                      {isKr ? "전체 목록 보기" : "Xem toàn bộ danh sách"}
                    </Link>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </div>

        {/* RIGHT SIDEBAR — Company Logo Scroller (xl+ only) */}
        <aside className="hidden xl:flex flex-shrink-0 w-[180px]" aria-label="Doanh nghiệp Hàn Quốc">
          <div className="sticky top-[72px] self-start w-[180px]" style={{ height: "calc(100vh - 72px)" }}>
            <SidebarCompanyScroller isKr={isKr} />
          </div>
        </aside>

      </div>

      {/* Lightbox */}
      {lightboxImg && (
        <div
          className="fixed inset-0 z-[300] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightboxImg(null)}
          role="dialog"
          aria-modal="true"
          aria-label="Xem ảnh phóng to"
        >
          <img
            src={lightboxImg}
            alt="Xem ảnh phóng to"
            className="max-w-full max-h-[90vh] rounded-xl object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            onClick={() => setLightboxImg(null)}
            className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full text-white cursor-pointer transition-colors"
            aria-label="Đóng"
          >
            <i className="ri-close-line text-xl" aria-hidden="true" />
          </button>
        </div>
      )}

      {/* Mobile Floating Button */}
      <Link
        to="/danh-muc"
        className="lg:hidden fixed bottom-4 right-4 z-50 flex items-center gap-2 bg-[#004E98] text-white px-4 py-3 rounded-full border-2 border-[#10B981] shadow-lg hover:shadow-xl transition-all cursor-pointer"
      >
        <div className="w-5 h-5 flex items-center justify-center">
          <i className="ri-home-line text-sm" />
        </div>
        <span className="text-sm font-semibold whitespace-nowrap">Sản phẩm & Đối tác</span>
      </Link>

      <Footer />
    </div>
  );
};

export default KoreanBriefDetailPage;
